<template>
  <div class="location-highlights-page">
    <div v-if="isLoading" style="margin-bottom: 35px; display: flex; flex-direction: column; align-items: center; text-align: center">
      <LoaderNew style="width: 70px; height: 70px; margin-bottom: 10px" :borderWidth="3" />
    </div>

    <div v-else-if="noCalendarsFound" class="no-calendars-message" style="margin-bottom: 35px; display: flex; flex-direction: column; align-items: center; text-align: center">
      <h2 class="global-h2">No Calendars Found</h2>
      <p class="global-p">Please contact Shrpa to get started.</p>
    </div>

    <template v-else>
      <!-- Header -->
      <div class="location-highlights-page__header">
        <div class="location-highlights-page__header-copy">
          <CalendarPicker :customerId="customerId" suffix="Local Promotion" />
          <p class="global-p location-highlights-page__subtitle">Show how your social content supports local businesses, attractions, and events.</p>
          <!-- <div v-if="isCalendarLineVisible" class="location-highlights-page__calendar-line">Calendar: {{ calendarDisplayName }}</div> -->
        </div>

        <div class="location-highlights-page__filters">
          <LoadingIcon v-if="isRefreshing" :size="16" />
          <ReportFilterDropdown label="Posts" :options="POST_FILTER_OPTIONS" :modelValue="postFilter" @update:modelValue="value => (postFilter = value as LocationHighlightsPostFilter)" />
          <ReportFilterDropdown label="Date range" :options="DATE_RANGE_OPTIONS" :modelValue="dateRange" @update:modelValue="value => (dateRange = value as LocationHighlightsDateRange)" />
        </div>
      </div>

      <!-- Load error -->
      <NoteWithIcon v-if="loadError" class="location-highlights-page__note" color="red" size="small">
        <template #text>
          <div>{{ loadError }}</div>
          <LinkWithIcon class="location-highlights-page__retry" isDottedUnderline color="red" @click="loadReport(true)">Try again</LinkWithIcon>
        </template>
      </NoteWithIcon>

      <!-- Report body -->
      <template v-if="report">
        <!-- No taggable accounts saved yet -->
        <NoteWithIcon v-if="report.locationCount === 0" class="location-highlights-page__note" color="gray" size="small">
          <template #text>
            <div>
              No taggable accounts saved yet.
              <router-link :to="{ name: 'CalendarConfig', params: { customerId } }">Add locations in Configuration</router-link>
              to start tracking local promotion.
            </div>
          </template>
        </NoteWithIcon>

        <template v-else>
          <ReportKpiTiles class="location-highlights-page__tiles" :tiles="kpiTiles" />

          <NoteWithIcon v-if="report.postCount === 0" class="location-highlights-page__note" color="gray" size="small">
            <template #text><div>No posts match these filters</div></template>
          </NoteWithIcon>

          <div class="location-highlights-page__cards-row">
            <MostHighlightedLocations :rows="topHighlightedRows" />
            <NotYetHighlightedLocations :rows="notYetRows" @viewAll="handleViewAll" />
            <LocationCoverageBar :highlightedCount="report.locationsHighlightedCount" :totalCount="report.locationCount" />
          </div>

          <LocationHighlightsTable ref="tableRef" :rows="report.rows" :activeTab="activeTab" @update:activeTab="value => (activeTab = value)" />
        </template>
      </template>
    </template>

    <div style="margin-top: 50px; display: flex; justify-content: flex-end; width: 100%">
      <SuperUserActionPanel>
        <div>
          <SrpButton color="gray" fill="outlined" size="tiny" :to="{ name: 'LocationSocialMetricsReport', params: { customerId } }">Social Analytics</SrpButton>
        </div>
      </SuperUserActionPanel>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useHead } from "@unhead/vue";
import { MetricSender } from "@/helpers/MetricSender";
import { getGlobalRemoteLogger } from "@/helpers/RemoteLogger";
import AdminContext from "@logic/AdminContext";
import DayjsExtended from "@logic/DayjsExtended";
import { getShowTestCalendars } from "@logic/calendarManager";
import ContentCalendarRepo from "@repos/ContentCalendarRepo";
import SocialReportsRepo from "@repos/SocialReportsRepo";

// Components
import CalendarPicker from "@components/CalendarPicker.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import LoaderNew from "@components/Loader/LoaderNew.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import SuperUserActionPanel from "@components/SuperUserActionPanel.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import LocationCoverageBar from "./LocationHighlights/LocationCoverageBar.vue";
import LocationHighlightsTable from "./LocationHighlights/LocationHighlightsTable.vue";
import MostHighlightedLocations from "./LocationHighlights/MostHighlightedLocations.vue";
import NotYetHighlightedLocations from "./LocationHighlights/NotYetHighlightedLocations.vue";
import ReportFilterDropdown from "./shared/ReportFilterDropdown.vue";
import ReportKpiTiles from "./shared/ReportKpiTiles.vue";

// Types
import type { ReportKpiTile } from "./shared/ReportKpiTiles.vue";
import type { LocationHighlightRow, LocationHighlightsDateRange, LocationHighlightsPostFilter, LocationHighlightsReport } from "@contracts/socialReports";

defineOptions({
  name: "LocationHighlightsReport",
});

// Router
const route = useRoute();

// Normalize route params ------------------------------------------------------
const customerId = computed<string>(() => (Array.isArray(route.params.customerId) ? route.params.customerId[0] : (route.params.customerId as string)));

const pageTitle = ref("Local Promotion Overview");
useHead({ title: () => pageTitle.value });

// Filter options =============================================================
const POST_FILTER_OPTIONS = [
  { value: "All", label: "All" },
  { value: "OnlyApproved", label: "Only Approved" },
  { value: "OnlyPublished", label: "Only Published" },
];

const DATE_RANGE_OPTIONS = [
  { value: "ever", label: "Ever" },
  { value: "lastYear", label: "Last Year" },
  { value: "lastQuarter", label: "Last Quarter" },
  { value: "lastMonth", label: "Last Month" },
];

// State ======================================================================
const report = ref<LocationHighlightsReport | null>(null);
const postFilter = ref<LocationHighlightsPostFilter>("All");
const dateRange = ref<LocationHighlightsDateRange>("ever");
const activeTab = ref<"highlighted" | "notYet" | "all">("highlighted");
const isLoading = ref<boolean>(true);
const isRefreshing = ref<boolean>(false);
const loadError = ref<string>("");

const noCalendarsFound = ref<boolean>(false);

const tableRef = ref<InstanceType<typeof LocationHighlightsTable> | null>(null);

// KPI tiles ==================================================================
const kpiTiles = computed<ReportKpiTile[]>(() => {
  if (!report.value) return [];
  return [
    { label: "Locations highlighted", value: `${report.value.locationsHighlightedCount}` },
    { label: "Posts with locations", value: `${report.value.postsWithLocationsCount}` },
    { label: "Total highlights", value: `${report.value.totalHighlightCount}` },
    { label: "Location coverage", value: `${report.value.locationCoveragePercent}%`, isHidden: true },
  ];
});

// Derived rows ===============================================================
const topHighlightedRows = computed<LocationHighlightRow[]>(() => (report.value?.rows ?? []).filter(r => r.postCount > 0).slice(0, 4));

const notYetRows = computed<LocationHighlightRow[]>(() => (report.value?.rows ?? []).filter(r => r.postCount === 0).sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")));

// Date range -> params =======================================================
function getDateParams(): { startDate?: string; endDate?: string } {
  if (dateRange.value === "ever") return {};

  const today = DayjsExtended();
  const endDate = today.format("YYYY-MM-DD");

  if (dateRange.value === "lastYear") return { startDate: today.subtract(1, "year").format("YYYY-MM-DD"), endDate };
  if (dateRange.value === "lastQuarter") return { startDate: today.subtract(3, "month").format("YYYY-MM-DD"), endDate };
  return { startDate: today.subtract(1, "month").format("YYYY-MM-DD"), endDate };
}

// Load =======================================================================
async function loadReport(isRefresh: boolean) {
  if (isRefresh) isRefreshing.value = true;
  loadError.value = "";

  const { startDate, endDate } = getDateParams();

  try {
    report.value = await SocialReportsRepo.getLocationHighlights(customerId.value, postFilter.value, startDate, endDate);
  } catch (error) {
    getGlobalRemoteLogger()?.error(`LocationHighlightsPage.loadReport failed. customerId=${customerId.value} postFilter=${postFilter.value} error=${error}`);
    loadError.value = "We couldn't load this report.";
  } finally {
    isRefreshing.value = false;
    isLoading.value = false;
  }
}

watch([postFilter, dateRange], () => loadReport(true));

// "View all" from the Not yet highlighted card ===============================
async function handleViewAll() {
  activeTab.value = "notYet";
  tableRef.value?.clearSearch();
  await nextTick();
  tableRef.value?.$el?.scrollIntoView({ behavior: "smooth", block: "start" });
}

onMounted(async () => {
  const org = await AdminContext.setOrgInContextFromCustomerId(customerId.value);
  if (org?.name) pageTitle.value = `Local Promotion Overview - ${org.name}`;

  const calendars = await ContentCalendarRepo.getCalendarsForCustomer(customerId.value, getShowTestCalendars());
  noCalendarsFound.value = calendars.length === 0;

  if (noCalendarsFound.value) {
    isLoading.value = false;
    return;
  }

  MetricSender.sendMetric("Cust-Report-LocationHighlights-PageView", { customerId: customerId.value });

  await loadReport(false);
});
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

// Location highlights page ===================================================
.location-highlights-page {
  width: $desktop-wide-right-col-width;
  margin: 0 auto;

  &__header {
    margin-bottom: 22px;
    gap: 20px;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    flex-wrap: wrap;
  }

  &__header-copy {
    min-width: 0;
    flex-grow: 1;
  }

  &__subtitle {
    margin: 7px 0 0;
  }

  &__calendar-line {
    margin-top: 6px;
    color: #667085;
    font: 400 14px/18px sans-serif;
  }

  &__filters {
    gap: 18px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    padding-bottom: 4px;
  }

  &__note {
    margin-bottom: 22px;
  }

  &__retry {
    margin-top: 6px;
  }

  &__tiles {
    margin-bottom: 20px;
  }

  &__cards-row {
    margin-bottom: 20px;
    gap: 20px;
    display: grid;
    grid-template-columns: 1fr 1fr 1.4fr;
    align-items: stretch;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
  .location-highlights-page {
    width: $desktop-right-col-width;
  }
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .location-highlights-page {
    width: $laptop-right-col-width;
  }
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .location-highlights-page {
    width: $tablet-large-content-width;
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .location-highlights-page {
    width: $tablet-content-width;

    &__cards-row {
      grid-template-columns: minmax(0, 1fr);
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .location-highlights-page {
    width: $mobile-content-width;

    &__cards-row {
      grid-template-columns: minmax(0, 1fr);
    }
  }
}
</style>
